class CurlHub {
  constructor(root) {
    if (!root) return;

    if (root.dataset.curlHubInitialized === 'true') {
      return;
    }

    root.dataset.curlHubInitialized = 'true';

    this.root = root;
    this.section = root.closest('section');
    this.sectionId = root.dataset.sectionId;

    this.hubUrl =
      root.dataset.hubUrl ||
      '/pages/curl-hub';

    this.initialTopic =
      root.dataset.initialTopic ||
      'all';

    this.perPage =
      parseInt(root.dataset.perPage, 10) ||
      12;

    this.articles = [];

    this.tabs = [];
    this.grid = null;
    this.pagination = null;
    this.countEl = null;
    this.emptyEl = null;
    this.clearBtn = null;
    this.searchForm = null;
    this.searchInput = null;
    this.headingEl = null;

    this.handlePopState =
      this.handlePopState.bind(this);

    this.getElements();
    this.getArticleData();
    this.bindEvents();

    const normalizedState = this.render(
      this.readState(),
      {
        skipHistory: true
      }
    );

    /*
      Clean old URLs such as:

      /blogs/curl-edu?topic=tags-seasonal-ingredients

      or old Curl Hub topic URLs such as:

      /pages/curl-hub?topic=curls-101

      The real destination URL is used instead.
    */
    this.normalizeInitialUrl(
      normalizedState
    );
  }


  /* ====================================
     DOM ELEMENTS
  ==================================== */

  getElements() {
    if (!this.section) {
      return;
    }

    this.tabs = Array.from(
      this.section.querySelectorAll(
        '[data-curl-hub-tab]'
      )
    );

    this.grid =
      this.section.querySelector(
        `#CurlHubGrid-${this.sectionId}`
      );

    this.pagination =
      this.section.querySelector(
        `#CurlHubPagination-${this.sectionId}`
      );

    this.countEl =
      this.section.querySelector(
        `#CurlHubCount-${this.sectionId}`
      );

    this.emptyEl =
      this.section.querySelector(
        `#CurlHubEmpty-${this.sectionId}`
      );

    this.clearBtn =
      this.section.querySelector(
        `#CurlHubClear-${this.sectionId}`
      );

    this.searchForm =
      this.section.querySelector(
        '[data-curl-hub-search]'
      );

    this.searchInput =
      this.searchForm
        ? this.searchForm.querySelector(
            'input[type="search"]'
          )
        : null;

    this.headingEl =
      this.section.querySelector(
        '[data-curl-hub-heading]'
      );
  }


  /* ====================================
     ARTICLE DATA
  ==================================== */

  getArticleData() {
    const dataEl = this.section
      ? this.section.querySelector(
          `#CurlHubData-${this.sectionId}`
        )
      : document.getElementById(
          `CurlHubData-${this.sectionId}`
        );

    if (!dataEl) {
      console.warn(
        `Curl Hub: Article JSON not found for section ${this.sectionId}`
      );

      this.articles = [];
      return;
    }

    try {
      const rawData =
        dataEl.textContent.trim();

      this.articles =
        rawData
          ? JSON.parse(rawData)
          : [];
    } catch (error) {
      console.error(
        `Curl Hub: Unable to parse article data for section ${this.sectionId}.`,
        error
      );

      this.articles = [];
    }
  }


  /* ====================================
     EVENTS
  ==================================== */

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener(
        'click',
        (event) => {

          /*
            Preserve normal browser behavior
            for:
            - Ctrl/Cmd click
            - Shift click
            - middle click
            - open in new tab
          */
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          event.preventDefault();

          const topic =
            tab.dataset.topic ||
            'all';

          this.applyState({
            topic,
            page: 1
          });
        }
      );
    });


    if (this.searchForm) {
      this.searchForm.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();

          const value =
            this.searchInput
              ? this.searchInput.value.trim()
              : '';

          this.applyState({
            q: value,
            page: 1
          });
        }
      );
    }


    if (this.searchInput) {
      this.searchInput.addEventListener(
        'input',
        () => {

          if (
            this.searchInput.value.trim() === ''
          ) {
            const current =
              this.readState();

            if (current.q) {
              this.applyState({
                q: '',
                page: 1
              });
            }
          }

        }
      );
    }


    if (this.clearBtn) {
      this.clearBtn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          this.applyState({
            topic: 'all',
            q: '',
            page: 1
          });
        }
      );
    }


    window.addEventListener(
      'popstate',
      this.handlePopState
    );
  }


  handlePopState() {
    this.render(
      this.readState(),
      {
        skipHistory: true
      }
    );
  }


  /* ====================================
     DESTROY
  ==================================== */

  destroy() {
    window.removeEventListener(
      'popstate',
      this.handlePopState
    );

    if (this.root) {
      delete this.root.dataset
        .curlHubInitialized;

      delete this.root
        .curlHubInstance;
    }
  }


  /* ====================================
     URL HELPERS
  ==================================== */

  normalizePath(path) {
    let normalized =
      String(path || '/');

    if (
      normalized.length > 1 &&
      normalized.endsWith('/')
    ) {
      normalized =
        normalized.slice(0, -1);
    }

    return normalized;
  }


  getTabByTopic(topic) {
    return this.tabs.find(
      (tab) =>
        tab.dataset.topic === topic
    ) || null;
  }


  getTopicFromUrl() {
    const currentUrl =
      new URL(
        window.location.href
      );

    const currentPath =
      this.normalizePath(
        currentUrl.pathname
      );

    const currentTopicParam =
      currentUrl.searchParams.get(
        'topic'
      );


    /*
      FIRST:
      Match query-based routes.

      Example:
      /pages/curl-hub?topic=ingredients
    */

    for (const tab of this.tabs) {
      const href =
        tab.getAttribute('href');

      if (!href) continue;

      const tabUrl =
        new URL(
          href,
          window.location.origin
        );

      const tabPath =
        this.normalizePath(
          tabUrl.pathname
        );

      if (tabPath !== currentPath) {
        continue;
      }

      const expectedTopic =
        tabUrl.searchParams.get(
          'topic'
        );

      if (
        expectedTopic &&
        expectedTopic ===
          currentTopicParam
      ) {
        return (
          tab.dataset.topic ||
          'all'
        );
      }
    }


    /*
      SECOND:
      Match real blog URLs.

      Example:
      /blogs/curl-edu
      /blogs/seasonal

      A bad/legacy ?topic= parameter is
      intentionally ignored on blog URLs.
    */

    for (const tab of this.tabs) {
      const href =
        tab.getAttribute('href');

      if (!href) continue;

      const tabUrl =
        new URL(
          href,
          window.location.origin
        );

      const tabPath =
        this.normalizePath(
          tabUrl.pathname
        );

      if (tabPath !== currentPath) {
        continue;
      }

      const expectedTopic =
        tabUrl.searchParams.get(
          'topic'
        );

      if (expectedTopic) {
        continue;
      }


      if (
        currentPath.startsWith(
          '/blogs/'
        )
      ) {
        return (
          tab.dataset.topic ||
          'all'
        );
      }


      /*
        Curl Hub All route.
      */

      if (!currentTopicParam) {
        return (
          tab.dataset.topic ||
          'all'
        );
      }
    }


    /*
      Support an old Curl Hub URL temporarily:

      /pages/curl-hub?topic=curls-101

      It will later be normalized to:
      /blogs/curl-edu
    */

    if (currentTopicParam) {
      const legacyTab =
        this.getTabByTopic(
          currentTopicParam
        );

      if (legacyTab) {
        return currentTopicParam;
      }
    }


    return (
      this.initialTopic ||
      'all'
    );
  }


  /* ====================================
     READ STATE
  ==================================== */

  readState() {
    const params =
      new URLSearchParams(
        window.location.search
      );

    const rawPage =
      parseInt(
        params.get('page'),
        10
      );

    const page =
      Number.isFinite(rawPage) &&
      rawPage > 0
        ? rawPage
        : 1;

    return {
      topic:
        this.getTopicFromUrl(),

      q:
        params.get('q') ||
        '',

      page
    };
  }


  /* ====================================
     APPLY STATE
  ==================================== */

  applyState(partial) {
    const current =
      this.readState();

    const next = {
      ...current,
      ...partial
    };

    this.render(
      next,
      {
        skipHistory: false
      }
    );
  }


  /* ====================================
     BUILD REAL URL
  ==================================== */

  buildUrl(
    state,
    page = 1
  ) {
    const tab =
      this.getTabByTopic(
        state.topic
      ) ||
      this.getTabByTopic('all');

    const href =
      tab
        ? tab.getAttribute('href')
        : this.hubUrl;

    const url =
      new URL(
        href || this.hubUrl,
        window.location.origin
      );


    /*
      Keep the tab's permanent query
      parameter.

      Example Ingredients:
      ?topic=ingredients
    */


    /*
      Search is temporary state.
    */

    url.searchParams.delete('q');

    if (state.q) {
      url.searchParams.set(
        'q',
        state.q
      );
    }


    /*
      Pagination is a real URL.

      /blogs/curl-edu?page=2
    */

    url.searchParams.delete('page');

    if (
      page &&
      page > 1
    ) {
      url.searchParams.set(
        'page',
        String(page)
      );
    }


    return (
      url.pathname +
      url.search
    );
  }


  /* ====================================
     NORMALIZE LEGACY URL
  ==================================== */

  normalizeInitialUrl(state) {
    if (!state) return;

    const params =
      new URLSearchParams(
        window.location.search
      );

    const topicParam =
      params.get('topic');

    if (!topicParam) {
      return;
    }

    const path =
      this.normalizePath(
        window.location.pathname
      );

    const desired =
      this.buildUrl(
        state,
        state.page
      );

    const current =
      window.location.pathname +
      window.location.search;


    /*
      Bad topic query on a blog route:

      /blogs/curl-edu?topic=seasonal

      becomes:

      /blogs/curl-edu
    */

    if (
      path.startsWith('/blogs/') &&
      desired !== current
    ) {
      history.replaceState(
        state,
        '',
        desired
      );

      return;
    }


    /*
      Old query based topic URLs
      should normalize to their
      real blog destination.

      Ingredients is intentionally
      allowed to remain query-based.
    */

    if (
      topicParam !== 'ingredients' &&
      this.getTabByTopic(topicParam) &&
      desired !== current
    ) {
      history.replaceState(
        state,
        '',
        desired
      );
    }
  }


  /* ====================================
     FILTER ARTICLES
  ==================================== */

  filterArticles(state) {
    let list = [
      ...this.articles
    ];


    /* =================================
       ALL TAB
    ================================= */

    if (
      !state.topic ||
      state.topic === 'all'
    ) {

      const availableTags =
        this.tabs
          .filter((tab) => {

            const topic =
              tab.dataset.topic || '';

            const matchMode =
              tab.dataset.match ||
              'tag';

            const value =
              tab.dataset.value ||
              '';

            return (
              topic !== 'all' &&
              (
                matchMode === 'tag' ||
                matchMode ===
                  'multiple_tags'
              ) &&
              value.trim() !== ''
            );

          })
          .flatMap((tab) => {

            return String(
              tab.dataset.value || ''
            )
              .split(',')
              .map((tag) =>
                this.slugify(tag)
              )
              .filter(Boolean);

          });


      const availableTagSet =
        new Set(
          availableTags
        );


      list =
        list.filter(
          (article) => {

            const articleTags =
              Array.isArray(
                article.tags
              )
                ? article.tags
                : [];

            return articleTags.some(
              (tag) =>
                availableTagSet.has(
                  this.slugify(tag)
                )
            );

          }
        );

    }


    /* =================================
       CATEGORY
    ================================= */

    else {

      const activeTab =
        this.getTabByTopic(
          state.topic
        );


      if (activeTab) {

        const matchMode =
          activeTab.dataset.match ||
          'tag';

        const matchValue =
          activeTab.dataset.value ||
          '';


        /* Entire blog */

        if (
          matchMode === 'blog'
        ) {

          const wantedBlog =
            this.slugify(
              matchValue
            );

          list =
            list.filter(
              (article) => {

                const articleBlog =
                  this.slugify(
                    article.blogHandle
                  );

                return (
                  articleBlog ===
                  wantedBlog
                );

              }
            );

        }


        /* Multiple tags */

        else if (
          matchMode ===
          'multiple_tags'
        ) {

          const wantedTags =
            String(matchValue)
              .split(',')
              .map((tag) =>
                this.slugify(tag)
              )
              .filter(Boolean);

          const wantedTagSet =
            new Set(
              wantedTags
            );

          list =
            list.filter(
              (article) => {

                const tags =
                  Array.isArray(
                    article.tags
                  )
                    ? article.tags
                    : [];

                return tags.some(
                  (tag) =>
                    wantedTagSet.has(
                      this.slugify(tag)
                    )
                );

              }
            );

        }


        /* Single tag */

        else {

          const wantedTag =
            this.slugify(
              matchValue
            );

          list =
            list.filter(
              (article) => {

                const tags =
                  Array.isArray(
                    article.tags
                  )
                    ? article.tags
                    : [];

                return tags.some(
                  (tag) =>
                    this.slugify(tag) ===
                    wantedTag
                );

              }
            );

        }

      }

    }


    /* =================================
       SEARCH
    ================================= */

    if (state.q) {

      const needle =
        String(state.q)
          .trim()
          .toLowerCase();

      if (needle) {

        list =
          list.filter(
            (article) => {

              const tags =
                Array.isArray(
                  article.tags
                )
                  ? article.tags
                  : [];

              const haystack = [
                article.title || '',
                article.excerpt || '',
                article.blog || '',
                article.author || '',
                ...tags
              ]
                .join(' ')
                .toLowerCase();

              return haystack.includes(
                needle
              );

            }
          );

      }

    }


    return list;
  }


  /* ====================================
     SLUGIFY
  ==================================== */

  slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        '-'
      )
      .replace(
        /(^-|-$)/g,
        ''
      );
  }


  /* ====================================
     ESCAPE HTML
  ==================================== */

  escapeHtml(value) {
    const div =
      document.createElement(
        'div'
      );

    div.textContent =
      value == null
        ? ''
        : String(value);

    return div.innerHTML;
  }


  /* ====================================
     ARTICLE CARD
  ==================================== */

  cardMarkup(article) {
    let image = '';

    if (article.image) {

      const imageAlt =
        article.imageAlt ||
        article.title ||
        '';

      const widthAttribute =
        article.imageWidth
          ? ` width="${this.escapeHtml(
              article.imageWidth
            )}"`
          : '';

      const heightAttribute =
        article.imageHeight
          ? ` height="${this.escapeHtml(
              article.imageHeight
            )}"`
          : '';


      image = `
        <div class="aspect-w-4 aspect-h-3">
          <img
            src="${this.escapeHtml(article.image)}"
            alt="${this.escapeHtml(imageAlt)}"
            loading="lazy"
            decoding="async"
            ${widthAttribute}
            ${heightAttribute}
            class="w-full h-full object-cover"
          >
        </div>
      `;
    }


    let chip = '';

    if (
      Array.isArray(
        article.tags
      ) &&
      article.tags.length > 0
    ) {
      chip = `
        <span class="curl-hub-chip">
          ${this.escapeHtml(
            article.tags[0]
          )}
        </span>
      `;
    }


    return `
      <li class="curl-hub-card card">

        <a
          href="${this.escapeHtml(article.url)}"
          tabindex="-1"
          aria-hidden="true"
        >
          ${image}
        </a>

        <div class="curl-hub-card-body">

          ${chip}

          <h3 class="font-heading">
            <a href="${this.escapeHtml(article.url)}">
              ${this.escapeHtml(article.title)}
            </a>
          </h3>

          <p class="curl-hub-excerpt">
            ${this.escapeHtml(article.excerpt)}
          </p>

          <div class="curl-hub-card-foot">

            <span>
              ${this.escapeHtml(article.blog)}
              ·
              ${this.escapeHtml(article.date)}
            </span>

            <a href="${this.escapeHtml(article.url)}">
              Read more →
            </a>

          </div>

        </div>

      </li>
    `;
  }


  /* ====================================
     TAB UI
  ==================================== */

  updateTabs(state) {
    this.tabs.forEach(
      (tab) => {

        const isActive =
          tab.dataset.topic ===
          state.topic;

        tab.setAttribute(
          'aria-selected',
          isActive
            ? 'true'
            : 'false'
        );

        if (isActive) {
          tab.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          tab.removeAttribute(
            'aria-current'
          );
        }

      }
    );
  }


  /* ====================================
     H1
  ==================================== */

  updateHeading(state) {
    if (!this.headingEl) {
      return;
    }

    const tab =
      this.getTabByTopic(
        state.topic
      );

    if (
      tab &&
      tab.dataset.heading
    ) {
      this.headingEl.textContent =
        tab.dataset.heading;
    }
  }


  /* ====================================
     SEARCH INPUT
  ==================================== */

  updateSearchInput(state) {
    if (!this.searchInput) {
      return;
    }

    if (
      this.searchInput.value !==
      state.q
    ) {
      this.searchInput.value =
        state.q || '';
    }
  }


  /* ====================================
     CANONICAL
  ==================================== */

  updateCanonical(
    state,
    page
  ) {
    const canonical =
      document.querySelector(
        'link[rel="canonical"]'
      );

    if (!canonical) {
      return;
    }


    /*
      Search pages should canonicalize
      back to the actual topic landing
      page, not create unlimited SEO URLs.
    */

    const canonicalState = {
      ...state,
      q: ''
    };

    const canonicalPage =
      state.q
        ? 1
        : page;

    const relativeUrl =
      this.buildUrl(
        canonicalState,
        canonicalPage
      );

    const absoluteUrl =
      new URL(
        relativeUrl,
        window.location.origin
      ).href;

    canonical.setAttribute(
      'href',
      absoluteUrl
    );
  }


  /* ====================================
     SEARCH ROBOTS
  ==================================== */

  updateRobots(state) {
    let managedMeta =
      document.querySelector(
        'meta[name="robots"][data-curl-hub-managed]'
      );


    /*
      Search query combinations should
      not become indexable landing pages.
    */

    if (state.q) {

      if (!managedMeta) {
        managedMeta =
          document.createElement(
            'meta'
          );

        managedMeta.setAttribute(
          'name',
          'robots'
        );

        managedMeta.setAttribute(
          'data-curl-hub-managed',
          'true'
        );

        document.head.appendChild(
          managedMeta
        );
      }

      managedMeta.setAttribute(
        'content',
        'noindex,follow'
      );

      return;
    }


    /*
      Only remove the robots meta tag
      created by Curl Hub.
      Never remove a Shopify/theme
      robots directive.
    */

    if (managedMeta) {
      managedMeta.remove();
    }
  }


  /* ====================================
     RENDER
  ==================================== */

  render(
    state,
    {
      skipHistory = false
    } = {}
  ) {

    /*
      Invalid topic protection.
    */

    const validTopic =
      this.getTabByTopic(
        state.topic
      )
        ? state.topic
        : 'all';


    const safeState = {
      ...state,
      topic: validTopic,
      q: String(
        state.q || ''
      )
    };


    const filtered =
      this.filterArticles(
        safeState
      );


    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filtered.length /
          this.perPage
        )
      );


    const requestedPage =
      parseInt(
        safeState.page,
        10
      ) || 1;


    const page =
      Math.min(
        Math.max(
          1,
          requestedPage
        ),
        totalPages
      );


    const normalizedState = {
      ...safeState,
      page
    };


    const start =
      (page - 1) *
      this.perPage;


    const pageItems =
      filtered.slice(
        start,
        start + this.perPage
      );


    this.updateTabs(
      normalizedState
    );

    this.updateHeading(
      normalizedState
    );

    this.updateSearchInput(
      normalizedState
    );


    /* Article count */

    if (this.countEl) {

      const label =
        filtered.length === 1
          ? 'article'
          : 'articles';

      if (normalizedState.q) {
        this.countEl.textContent =
          `${filtered.length} ${label} for “${normalizedState.q}”`;
      } else {
        this.countEl.textContent =
          `${filtered.length} ${label}`;
      }

    }


    /* Clear filters */

    if (this.clearBtn) {
      this.clearBtn.hidden =
        normalizedState.topic ===
          'all' &&
        !normalizedState.q;
    }


    /* Empty */

    if (
      pageItems.length === 0
    ) {

      if (this.grid) {
        this.grid.hidden = true;
        this.grid.innerHTML = '';
      }

      if (this.pagination) {
        this.pagination.hidden = true;
        this.pagination.innerHTML = '';
      }

      if (this.emptyEl) {
        this.emptyEl.hidden = false;
      }

    }


    /* Results */

    else {

      if (this.emptyEl) {
        this.emptyEl.hidden = true;
      }

      if (this.grid) {
        this.grid.hidden = false;

        this.grid.innerHTML =
          pageItems
            .map(
              (article) =>
                this.cardMarkup(
                  article
                )
            )
            .join('');
      }

      this.renderPagination(
        normalizedState,
        page,
        totalPages
      );

    }


    this.updateCanonical(
      normalizedState,
      page
    );

    this.updateRobots(
      normalizedState
    );


    /* History */

    if (!skipHistory) {

      const url =
        this.buildUrl(
          normalizedState,
          page
        );

      const currentUrl =
        window.location.pathname +
        window.location.search;


      /*
        Don't create duplicate browser
        history entries.
      */

      if (url !== currentUrl) {

        history.pushState(
          normalizedState,
          '',
          url
        );

      }

    }


    return normalizedState;
  }


  /* ====================================
     PAGINATION RANGE
  ==================================== */

  paginationRange(
    current,
    total,
    delta = 2
  ) {
    const pages = [1];

    for (
      let i = current - delta;
      i <= current + delta;
      i += 1
    ) {
      if (
        i > 1 &&
        i < total
      ) {
        pages.push(i);
      }
    }

    if (total > 1) {
      pages.push(total);
    }

    const sorted =
      Array.from(
        new Set(pages)
      ).sort(
        (a, b) =>
          a - b
      );

    const withDots = [];
    let previous;

    sorted.forEach((p) => {

      if (
        previous !== undefined
      ) {

        if (
          p - previous === 2
        ) {
          withDots.push(
            previous + 1
          );
        }

        else if (
          p - previous > 2
        ) {
          withDots.push(
            '...'
          );
        }

      }

      withDots.push(p);
      previous = p;

    });

    return withDots;
  }


  /* ====================================
     PAGINATION
  ==================================== */

  renderPagination(
    state,
    page,
    totalPages
  ) {
    if (!this.pagination) {
      return;
    }

    if (totalPages <= 1) {
      this.pagination.hidden = true;
      this.pagination.innerHTML = '';
      return;
    }

    this.pagination.hidden = false;

    const links = [];

    this.paginationRange(
      page,
      totalPages
    ).forEach((p) => {

      if (p === '...') {

        links.push(`
          <span
            class="curl-hub-pagination-ellipsis"
            aria-hidden="true"
          >&hellip;</span>
        `);

        return;
      }

      const current =
        p === page
          ? 'aria-current="page"'
          : '';

      links.push(`
        <a
          href="${this.escapeHtml(
            this.buildUrl(
              state,
              p
            )
          )}"
          data-page="${p}"
          ${current}
        >
          ${p}
        </a>
      `);

    });


    this.pagination.innerHTML =
      links.join('');


    this.pagination
      .querySelectorAll(
        'a[data-page]'
      )
      .forEach((link) => {

        link.addEventListener(
          'click',
          (event) => {

            if (
              event.defaultPrevented ||
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return;
            }

            event.preventDefault();

            const newPage =
              parseInt(
                link.dataset.page,
                10
              ) || 1;

            this.applyState({
              page: newPage
            });

            if (
              this.section &&
              typeof this.section
                .scrollIntoView ===
                'function'
            ) {
              this.section
                .scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
            }

          }
        );

      });
  }
}


/* ====================================
   INITIALIZATION
==================================== */

function initCurlHub(
  container = document
) {
  const roots = [];

  if (
    container.matches &&
    container.matches(
      '[data-curl-hub]'
    )
  ) {
    roots.push(container);
  }

  if (
    container.querySelectorAll
  ) {
    roots.push(
      ...container.querySelectorAll(
        '[data-curl-hub]'
      )
    );
  }

  roots.forEach((root) => {

    if (
      root.dataset
        .curlHubInitialized ===
      'true'
    ) {
      return;
    }

    const instance =
      new CurlHub(root);

    root.curlHubInstance =
      instance;

  });
}


/* ====================================
   NORMAL STOREFRONT LOAD
==================================== */

if (
  document.readyState ===
  'loading'
) {

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initCurlHub(document);
    },
    {
      once: true
    }
  );

} else {

  initCurlHub(document);

}


/* ====================================
   SHOPIFY THEME EDITOR
==================================== */

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initCurlHub(
      event.target
    );
  }
);


document.addEventListener(
  'shopify:section:reorder',
  () => {
    initCurlHub(document);
  }
);


document.addEventListener(
  'shopify:section:unload',
  (event) => {

    const roots = [];

    if (
      event.target.matches &&
      event.target.matches(
        '[data-curl-hub]'
      )
    ) {
      roots.push(
        event.target
      );
    }

    if (
      event.target.querySelectorAll
    ) {
      roots.push(
        ...event.target.querySelectorAll(
          '[data-curl-hub]'
        )
      );
    }

    roots.forEach((root) => {

      if (
        root.curlHubInstance &&
        typeof root
          .curlHubInstance
          .destroy ===
          'function'
      ) {
        root
          .curlHubInstance
          .destroy();
      }

    });

  }
);

